<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="card"
    />

    <template v-if="!isLoading">
      <!-- サマリーカード（折りたたみ） -->
      <v-expansion-panels
        v-if="recentCustomers.length > 0 || upcomingBirthdayCustomers.length > 0"
        flat
        class="mb-4 summary-panels"
      >
        <v-expansion-panel v-if="recentCustomers.length > 0">
          <v-expansion-panel-header class="py-2">
            <span class="text-subtitle-2">
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiAccountClock }}
              </v-icon>
              最近来店した顧客 ({{ recentCustomers.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <CustomerRecentVisits
              :customers="recentCustomers"
              @click="goToCustomerDetail"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-if="upcomingBirthdayCustomers.length > 0">
          <v-expansion-panel-header class="py-2">
            <span class="text-subtitle-2">
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiCake }}
              </v-icon>
              30日以内に誕生日の顧客 ({{ upcomingBirthdayCustomers.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <CustomerUpcomingBirthdays
              :customers="upcomingBirthdayCustomers"
              @click="goToCustomerDetail"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- 検索バー + 絞り込みボタン -->
      <div class="d-flex align-center mb-4">
        <v-text-field
          v-model="searchQuery"
          :prepend-inner-icon="icons.mdiMagnify"
          label="名前・会員番号で検索"
          single-line
          hide-details
          clearable
          dense
          outlined
          style="max-width: 400px;"
          class="mr-2"
          @input="handleSearchDebounced"
          @click:clear="handleClearSearch"
        />
        <v-btn
          outlined
          @click="filterDialog = true"
        >
          <v-icon left>
            {{ icons.mdiFilterVariant }}
          </v-icon>
          絞り込み
          <v-badge
            v-if="activeFilterCount > 0"
            :content="activeFilterCount"
            color="primary"
            inline
            class="ml-1"
          />
        </v-btn>
      </div>

      <!-- 適用中フィルターチップ -->
      <div
        v-if="activeFilterChips.length > 0"
        class="mb-4 d-flex flex-wrap"
        style="gap: 4px;"
      >
        <v-chip
          v-for="chip in activeFilterChips"
          :key="chip.key"
          small
          close
          outlined
          @click:close="removeFilter(chip.key)"
        >
          {{ chip.label }}
        </v-chip>
        <v-btn
          x-small
          text
          color="grey"
          @click="handleFilterReset"
        >
          すべて解除
        </v-btn>
      </div>

      <!-- 顧客一覧 -->
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>顧客一覧</span>
          <div>
            <v-btn
              color="secondary"
              outlined
              class="mr-2"
              @click="openAddPropertyDialog"
            >
              <v-icon left>
                {{ icons.mdiPlaylistPlus }}
              </v-icon>
              プロパティを追加
            </v-btn>
            <v-btn
              color="primary"
              @click="openNewCustomerDialog"
            >
              <v-icon left>
                {{ icons.mdiPlus }}
              </v-icon>
              顧客を追加
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <CustomerList
            :customers="customers"
            :custom-fields="customFields"
            :loading="isSearching"
            @click="handleCustomerClick"
            @create-table="handleCreateTableFromCustomer"
          />
        </v-card-text>
      </v-card>
    </template>

    <!-- 絞り込みダイアログ -->
    <v-dialog
      v-model="filterDialog"
      max-width="480"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-1">
              {{ icons.mdiFilterVariant }}
            </v-icon>
            絞り込み
          </span>
          <v-btn
            icon
            @click="filterDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh; overflow-y: auto;">
          <CustomerFilterPanel
            ref="filterPanel"
            :customer-tags="customerTags"
            @apply="handleFilterApplyAndClose"
            @reset="handleFilterResetAndClose"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 新規顧客追加ダイアログ -->
    <v-dialog
      v-if="newCustomerDialog"
      v-model="newCustomerDialog"
      width="500"
      persistent
    >
      <CustomerForm
        :type="'newUser'"
        @submit="handleCustomerCreate"
        @cancel="newCustomerDialog = false"
      />
    </v-dialog>

    <!-- 顧客からの卓作成ダイアログ -->
    <CreateTableFromCustomerDialog
      v-model="createTableDialog"
      :customer="createTableCustomer"
      @created="handleTableCreated"
    />

    <!-- プロパティ追加/編集ダイアログ -->
    <CustomerPropertyFormDialog
      v-model="propertyFormDialog"
      :property="editingProperty"
      @submit="handleAddProperty"
      @update="handleUpdateProperty"
    />

    <!-- プロパティ削除確認ダイアログ -->
    <CustomerPropertyDeleteConfirm
      v-model="deleteConfirmDialog"
      :property="deletingProperty"
      @confirm="handleDeleteProperty"
    />
  </div>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  onMounted,
  ref,
} from '@vue/composition-api'
import {
  mdiPlus, mdiMagnify, mdiPlaylistPlus, mdiFilterVariant,
  mdiClose, mdiAccountClock, mdiCake,
} from '@mdi/js'
import { debounce } from 'lodash'
import useCompInit from '@/views/composable/useCompInit'
import useCustomers from '@/views/v2-temp/common/customer-management/composables/useCustomers'
import useCustomerCustomFields from '@/views/v2-temp/common/customer-management/composables/useCustomerCustomFields'
import { SEGMENT_LABELS } from '@/views/v2-temp/common/customer-management/composables/useRfmAnalysis'
import CustomerForm from '@/views/v2-temp/common/customer-management/components/CustomerForm.vue'
import CustomerList from '@/views/v2-temp/common/customer-management/components/CustomerList.vue'
import CustomerFilterPanel from '@/views/v2-temp/common/customer-management/components/CustomerFilterPanel.vue'
import CustomerRecentVisits from '@/views/v2-temp/common/customer-management/components/CustomerRecentVisits.vue'
import CustomerUpcomingBirthdays from '@/views/v2-temp/common/customer-management/components/CustomerUpcomingBirthdays.vue'
import CreateTableFromCustomerDialog from '@/views/v2-temp/common/customer-management/components/CreateTableFromCustomerDialog.vue'
import CustomerPropertyFormDialog from '@/views/v2-temp/common/customer-management/components/CustomerPropertyFormDialog.vue'
import CustomerPropertyDeleteConfirm from '@/views/v2-temp/common/customer-management/components/CustomerPropertyDeleteConfirm.vue'
import CustomerTagApi from '@/api/waiter/CustomerTag'

export default {
  name: 'WaiterCustomerManagement',
  components: {
    CustomerForm,
    CustomerList,
    CustomerFilterPanel,
    CustomerRecentVisits,
    CustomerUpcomingBirthdays,
    CreateTableFromCustomerDialog,
    CustomerPropertyFormDialog,
    CustomerPropertyDeleteConfirm,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { initWith, isLoading } = useCompInit()

    const {
      customers,
      newCustomerDialog,
      searchQuery,
      isSearching,
      pagination,
      recentCustomers,
      upcomingBirthdayCustomers,
      activeFilters,
      getCustomers,
      searchCustomers,
      createCustomer,
      openNewCustomerDialog,
      getCustomersWithStatsFiltered,
      applyFilters,
      resetFilters,
    } = useCustomers(vm)

    const {
      customFields,
      propertyFormDialog,
      editingProperty,
      deleteConfirmDialog,
      deletingProperty,
      getCustomFields,
      addCustomField,
      updateCustomField,
      deleteCustomField,
      openAddPropertyDialog,
    } = useCustomerCustomFields(vm)

    // 顧客タグ一覧
    const customerTags = ref([])
    const getCustomerTags = async () => {
      try {
        const res = await CustomerTagApi.getCustomerTags()
        customerTags.value = (res.data?.customerTags?.data || []).map(t => ({
          id: t.id,
          name: t.attributes?.name || t.name,
        }))
      } catch (e) {
        // タグ取得失敗時は空のまま
      }
    }

    // 絞り込みダイアログ
    const filterDialog = ref(false)
    const appliedFilters = ref({})

    // 適用中フィルター数
    const activeFilterCount = computed(() => {
      const f = appliedFilters.value
      let count = 0
      if (f.rfmSegment && f.rfmSegment.length > 0) count += f.rfmSegment.length
      if (f.minVisitCount) count += 1
      if (f.lastVisitAfter || f.lastVisitBefore) count += 1
      if (f.customerTagIds && f.customerTagIds.length > 0) count += 1
      if (f.castIds && f.castIds.length > 0) count += 1
      if (f.nominationRecipientIds && f.nominationRecipientIds.length > 0) count += 1
      if (f.birthdayMonth) count += 1

      return count
    })

    // 適用中フィルターをチップ表示
    const activeFilterChips = computed(() => {
      const f = appliedFilters.value
      const chips = []

      if (f.rfmSegment && f.rfmSegment.length > 0) {
        f.rfmSegment.forEach(seg => {
          chips.push({ key: `rfm_${seg}`, label: SEGMENT_LABELS[seg] || seg })
        })
      }
      if (f.minVisitCount) {
        chips.push({ key: 'minVisitCount', label: `来店${f.minVisitCount}回以上` })
      }
      if (f.lastVisitAfter || f.lastVisitBefore) {
        const parts = []
        if (f.lastVisitAfter) parts.push(`${f.lastVisitAfter}~`)
        if (f.lastVisitBefore) parts.push(`~${f.lastVisitBefore}`)
        chips.push({ key: 'lastVisit', label: `最終来店: ${parts.join('')}` })
      }
      if (f.customerTagIds && f.customerTagIds.length > 0) {
        chips.push({ key: 'customerTags', label: `タグ: ${f.customerTagIds.length}件` })
      }
      if (f.castIds && f.castIds.length > 0) {
        chips.push({ key: 'casts', label: `関連キャスト: ${f.castIds.length}人` })
      }
      if (f.nominationRecipientIds && f.nominationRecipientIds.length > 0) {
        chips.push({ key: 'nominationRecipients', label: `指名受領: ${f.nominationRecipientIds.length}人` })
      }
      if (f.birthdayMonth) {
        chips.push({ key: 'birthday', label: `誕生日: ${f.birthdayMonth}月` })
      }

      return chips
    })

    const removeFilter = key => {
      const f = { ...appliedFilters.value }
      if (key.startsWith('rfm_')) {
        const seg = key.replace('rfm_', '')
        f.rfmSegment = (f.rfmSegment || []).filter(s => s !== seg)
      } else if (key === 'minVisitCount') {
        f.minVisitCount = null
        f.visitCountStartDate = null
        f.visitCountEndDate = null
      } else if (key === 'lastVisit') {
        f.lastVisitAfter = null
        f.lastVisitBefore = null
      } else if (key === 'customerTags') {
        f.customerTagIds = []
      } else if (key === 'casts') {
        f.castIds = []
      } else if (key === 'nominationRecipients') {
        f.nominationRecipientIds = []
      } else if (key === 'birthday') {
        f.birthdayMonth = null
      }
      appliedFilters.value = f
      applyFilters(f)
    }

    // 卓作成ダイアログ
    const createTableDialog = ref(false)
    const createTableCustomer = ref(null)
    const isCreateTableMode = ref(false)

    const handleCreateTableFromCustomer = customer => {
      createTableCustomer.value = customer
      createTableDialog.value = true
    }

    const handleTableCreated = () => {
      createTableDialog.value = false
      vm.$toast.success('卓を作成しました')
      vm.$router.push({
        name: 'waiter-halls',
        params: { club_slug: vm.$route.params.club_slug },
      })
    }

    const handleCustomerCreate = async event => {
      const success = await createCustomer(event)
      if (success) {
        newCustomerDialog.value = false
      }
    }

    // インクリメンタルサーチ（300msデバウンス）
    const handleSearchDebounced = debounce(() => {
      searchCustomers(searchQuery.value, { page: 1, perPage: pagination.value.perPage })
    }, 300)

    const handleClearSearch = () => {
      searchQuery.value = ''
      getCustomers({ page: 1, perPage: pagination.value.perPage })
    }

    const handleFilterApplyAndClose = filters => {
      appliedFilters.value = { ...filters }
      applyFilters(filters)
      filterDialog.value = false
    }

    const handleFilterResetAndClose = () => {
      appliedFilters.value = {}
      resetFilters()
      filterDialog.value = false
    }

    const handleFilterReset = () => {
      appliedFilters.value = {}
      resetFilters()
    }

    const goToCustomerDetail = customer => {
      vm.$router.push({
        name: 'waiter-settings-customer-management-show',
        params: {
          club_slug: vm.$route.params.club_slug,
          customerId: customer.id,
        },
      })
    }

    // 顧客クリック: create_table モード時はダイアログ、通常は詳細
    const handleCustomerClick = customer => {
      if (isCreateTableMode.value) {
        handleCreateTableFromCustomer(customer)
      } else {
        goToCustomerDetail(customer)
      }
    }

    const handleAddProperty = event => {
      addCustomField(event)
    }

    const handleUpdateProperty = event => {
      updateCustomField(event.id, event)
    }

    const handleDeleteProperty = event => {
      deleteCustomField(deletingProperty.value.id, event)
    }

    onMounted(() => {
      // create_table モードの検出
      isCreateTableMode.value = vm.$route.query.mode === 'create_table'

      initWith([
        getCustomers(),
        getCustomFields(),
        getCustomerTags(),
        getCustomersWithStatsFiltered(),
      ])
    })

    return {
      isLoading,
      isSearching,
      customers,
      newCustomerDialog,
      searchQuery,
      pagination,
      customFields,
      customerTags,
      recentCustomers,
      upcomingBirthdayCustomers,
      activeFilters,
      createTableDialog,
      createTableCustomer,
      filterDialog,
      activeFilterCount,
      activeFilterChips,
      propertyFormDialog,
      editingProperty,
      deleteConfirmDialog,
      deletingProperty,
      openNewCustomerDialog,
      openAddPropertyDialog,
      handleCustomerCreate,
      handleAddProperty,
      handleUpdateProperty,
      handleDeleteProperty,
      handleSearchDebounced,
      handleClearSearch,
      handleFilterApplyAndClose,
      handleFilterResetAndClose,
      handleFilterReset,
      handleCustomerClick,
      handleCreateTableFromCustomer,
      handleTableCreated,
      removeFilter,
      goToCustomerDetail,
      icons: {
        mdiPlus,
        mdiMagnify,
        mdiPlaylistPlus,
        mdiFilterVariant,
        mdiClose,
        mdiAccountClock,
        mdiCake,
      },
    }
  },
}
</script>

<style scoped>
.summary-panels {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
</style>
